import PropTypes from "prop-types";
import React from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  ScrollView,
} from "react-native";
import ENV from "app/utils/ENV";
import colorHelpers from "app/utils/colorHelpers";
import Text from "app/components/Text";
import { styleSheet, colors, sizes, screenRatio } from "app/utils/styleSheet";

import { MaterialCommunityIcons, AntDesign, Feather } from "@expo/vector-icons";

import { StyleContext } from "app/contexts/Style";

export default class KeyboardShortcuts extends React.Component {
  static contextType = StyleContext;

  // one keycap-styled chip per key, e.g. renderKeys("⌘", "Enter")
  renderKeys = (...keys) => (
    <Text>
      {keys.map((k, i) => (
        <Text key={i}>
          <Text style={this.styles().icon}>{k}</Text>
          {i < keys.length - 1 ? " " : ""}
        </Text>
      ))}
    </Text>
  );

  renderRow = (label, keys, subtext = null) => (
    <View style={this.styles().row}>
      <View style={{ flex: 1, paddingRight: this.context.sizes.base }}>
        <Text>{label}</Text>
        {!!subtext && <Text style={this.styles().subtext}>{subtext}</Text>}
      </View>
      {keys}
    </View>
  );

  renderSyntaxRow = (syntax, label, subtext = null) => (
    <View style={this.styles().row}>
      <View style={{ flex: 1, paddingRight: this.context.sizes.base }}>
        <Text>{label}</Text>
        {!!subtext && <Text style={this.styles().subtext}>{subtext}</Text>}
      </View>
      <Text style={this.styles().syntax}>{syntax}</Text>
    </View>
  );

  render() {
    return (
      <View style={this.styles().wrapper}>
        <ScrollView>
          <TouchableOpacity onPress={this.props.onHide}>
            <View style={this.styles().topHeader}>
              <Text bold style={{ fontSize: this.context.sizes.large }}>
                Keyboard Shortcuts
              </Text>
              <Text>
                <Feather
                  style={{ fontSize: this.context.sizes.large }}
                  name="x"
                />
              </Text>
            </View>
          </TouchableOpacity>

          <Text style={this.styles().header}>Global</Text>

          {this.renderRow("Toggle this panel", this.renderKeys("⌘", "/"))}
          {this.renderRow("Global search", this.renderKeys("⌘", "J"))}
          {this.renderRow("Back to top level", this.renderKeys("⌘", "P"))}
          {this.renderRow("Cycle details mode", this.renderKeys("⌘", "'"))}

          <View style={this.styles().gap} />

          <Text style={this.styles().header}>Composer</Text>

          {this.renderRow("Show suggestions", this.renderKeys("Tab"))}
          {this.renderRow(
            "Select suggestion",
            this.renderKeys("Enter"),
            "(when a suggestion is highlighted)",
          )}
          {this.renderRow("Next suggestion", this.renderKeys("↓"))}
          {this.renderRow("Create value", this.renderKeys("⌘", "Enter"))}
          {this.renderRow("Create note", this.renderKeys("⇧", "Enter"))}
          {this.renderRow(
            "Tag a parent",
            this.renderKeys("#", "Enter"),
            "(type # then search)",
          )}
          {this.renderRow("Clear / dismiss", this.renderKeys("Esc"))}

          <View style={this.styles().gap} />

          <Text style={this.styles().header}>Items in a chapter</Text>

          {this.renderRow(
            "Log progress",
            this.renderKeys("Enter"),
            "(when empty)",
          )}
          {this.renderRow("Complete item", this.renderKeys("⌘", "Enter"))}
          {this.renderRow(
            "Cycle category",
            this.renderKeys("⇧", "Enter"),
            "(topic → project → event)",
          )}
          {this.renderRow("Pin to today", this.renderKeys("⌥", "Enter"))}
          {this.renderRow(
            "Expand / collapse",
            this.renderKeys("⌘", "↓ / ↑"),
          )}
          {this.renderRow("Reorder item", this.renderKeys("⌘", "⌃", "↑ / ↓"))}
          {this.renderRow(
            "Send to top / bottom",
            this.renderKeys("⌘", "⌃", "⇧", "↑ / ↓"),
          )}
          {this.renderRow(
            "Open item dashboard",
            this.renderKeys("⌘", "⌃", "→"),
            "(add ⇧ for a new tab)",
          )}
          {this.renderRow(
            "Pin to parent chapter",
            this.renderKeys("⌃", "⌥", "↑"),
          )}
          {this.renderRow(
            "Copy to next chapter",
            this.renderKeys("⌘", "⇧", "→"),
          )}
          {this.renderRow("Duplicate item", this.renderKeys("⌘", "D"))}
          {this.renderRow(
            "Remove from chapter",
            this.renderKeys("⌘", "Delete"),
          )}
          {this.renderRow(
            "Snooze for a week",
            this.renderKeys("⌘", "⇧", "Delete"),
          )}
          {this.renderRow(
            "Focus previous / next",
            this.renderKeys("↑ / ↓"),
          )}

          <View style={this.styles().gap} />

          <Text style={this.styles().header}>Text editing</Text>
          <Text style={this.styles().sectionNote}>
            In scratchpads and moment bodies
          </Text>

          {this.renderRow(
            "Indent / unindent line",
            this.renderKeys("Tab / ⇧ Tab"),
          )}
          {this.renderRow(
            "Move line up / down",
            this.renderKeys("⌘", "⌃", "↑ / ↓"),
          )}
          {this.renderRow("Select lines", this.renderKeys("⇧", "↑ / ↓"))}
          {this.renderRow("Delete line", this.renderKeys("⌘", "Delete"))}
          {this.renderRow(
            "Strike out line(s)",
            this.renderKeys("⌘", "K"),
            "(toggles ~strikethrough~)",
          )}
          {this.renderRow("Complete item", this.renderKeys("⌘", "Enter"))}
          {this.renderRow("Insert timestamp", this.renderKeys("⌘", "⇧", "T"))}
          {this.renderRow(
            "Reference an item",
            this.renderKeys("#", "Enter"),
            "(type # then search)",
          )}
          {this.renderRow("Stop editing", this.renderKeys("Esc"))}

          <View style={this.styles().gap} />

          <Text style={this.styles().header}>Chapters</Text>

          {this.renderRow(
            "Focus previous / next",
            this.renderKeys("⌥", "← / →"),
          )}
          {this.renderRow(
            "Collapse / expand",
            this.renderKeys("⌥", "↑ / ↓"),
          )}

          <View style={this.styles().gap} />

          <Text style={this.styles().header}>Formatting</Text>
          <Text style={this.styles().sectionNote}>
            Markdown-like syntax, rendered in scratchpads and moment bodies
          </Text>

          {this.renderSyntaxRow("# text", "Heading", "(## and ### for smaller)")}
          {this.renderSyntaxRow(
            "- text",
            "Bullet list",
            "(bullet style cycles as you indent; Enter continues the list)",
          )}
          {this.renderSyntaxRow(
            "1. text",
            "Numbered list",
            "(Enter continues the numbering; Enter on an empty item ends it)",
          )}
          {this.renderSyntaxRow(
            "> Title",
            "Collapsible section",
            "(indent the body lines below; tap ▸ to toggle, cursor on the title expands)",
          )}
          {this.renderSyntaxRow("*text*", "Bold")}
          {this.renderSyntaxRow("_text_", "Italic")}
          {this.renderSyntaxRow("__text__", "Underline")}
          {this.renderSyntaxRow("~text~", "Strikethrough")}
          {this.renderSyntaxRow("`text`", "Inline code")}
          {this.renderSyntaxRow("```", "Code block", "(fence lines above and below)")}
          {this.renderSyntaxRow(
            "{123}",
            "Link an item",
            "(by id; shows its title, struck through once completed)",
          )}
          {this.renderSyntaxRow(
            "{123|label}",
            "Link with custom label",
          )}
          {this.renderSyntaxRow(
            "/{text}/",
            "Literal braces",
            "(escapes a reference, no link)",
          )}
          {this.renderSyntaxRow("<url>", "Hyperlink")}
          {this.renderSyntaxRow("[title](url)", "Named hyperlink")}

          <View style={this.styles().gap} />
        </ScrollView>
      </View>
    );
  }

  styles = () => ({
    wrapper: {
      width: 300,
      borderRadius: this.context.sizes.smallBorderRadius,
      position: "fixed",
      position: "absolute",
      right: 0,
      top: 0,
      bottom: 0,
      backgroundColor: colorHelpers.hexToRgbaString(
        this.context.colors.empty,
        0.91
      ),
      backgroundColor: this.context.colors.fill,
      padding: this.context.sizes.large,
      paddingTop: 0,
      borderLeftWidth: 1,
      borderColor: this.context.colors.trim,
    },
    topHeader: {
      height: 65,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      // borderWidth: 2,
      borderColor: "red",
      paddingHorizontal: this.context.sizes.small,
    },
    header: {
      // borderBottomWidth: 1,
      borderColor: this.context.colors.trim,
      fontWeight: "bold",
      marginBottom: this.context.sizes.base,
      borderRadius: this.context.sizes.smallBorderRadius,
      backgroundColor: colorHelpers.hexToRgbaString(
        this.context.colors.fill,
        0.8
      ),
      backgroundColor: colorHelpers.hexToRgbaString(
        this.context.colors.trim,
        0.6
      ),
      padding: this.context.sizes.small,
    },
    sectionNote: {
      opacity: 0.6,
      fontStyle: "italic",
      marginBottom: this.context.sizes.base,
      paddingHorizontal: this.context.sizes.small,
    },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      // alignItems: "center",
      marginBottom: this.context.sizes.base,
      paddingHorizontal: this.context.sizes.small,
      // borderBottomWidth: 1,
      borderColor: this.context.colors.fill,
      paddingBottom: this.context.sizes.small,
    },
    gap: {
      height: this.context.sizes.large,
    },
    icon: {
      fontSize: this.context.sizes.default,
      color: this.context.colors.foreground,
      // backgroundColor: colorHelpers.hexToRgbaString(this.context.colors.fill, 0.8),
      backgroundColor: colorHelpers.hexToRgbaString(
        this.context.colors.trim,
        0.6
      ),
      borderRadius: this.context.sizes.smallBorderRadius,
      padding: 3,
    },
    syntax: {
      fontSize: this.context.sizes.default - 1,
      fontFamily: "Courier New",
      color: this.context.colors.foreground,
      backgroundColor: colorHelpers.hexToRgbaString(
        this.context.colors.trim,
        0.6
      ),
      borderRadius: this.context.sizes.smallBorderRadius,
      padding: 3,
      alignSelf: "flex-start",
    },
    subtext: {
      opacity: 0.6,
      fontStyle: "italic",
    },
  });
}
